import React, { useEffect, useState } from "react";
import { Save, Loader2, UploadCloud, Trash2, FileText, ClipboardList } from "lucide-react";
import { adminApi, mediaUrl } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { useToast } from "../../hooks/use-toast";

const Field = ({ label, ...props }) => (
  <div>
    <Label className="text-navy font-semibold text-sm">{label}</Label>
    <Input className="mt-1" {...props} />
  </div>
);
const Area = ({ label, ...props }) => (
  <div>
    <Label className="text-navy font-semibold text-sm">{label}</Label>
    <Textarea className="mt-1" {...props} />
  </div>
);

export default function HealthCheckManager() {
  const { toast } = useToast();
  const [tab, setTab] = useState("config");
  const [cfg, setCfg] = useState(null);
  const [leads, setLeads] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => adminApi.healthCheck.get().then(setCfg).catch(() => {});
  useEffect(() => { load(); adminApi.healthCheck.leads().then(setLeads).catch(() => {}); }, []);

  if (!cfg) return <div className="text-slate-400 p-6">Loading…</div>;

  const set = (k, v) => setCfg({ ...cfg, [k]: v });
  const setArea = (idx, k, v) => {
    const areas = [...cfg.areas];
    areas[idx] = { ...areas[idx], [k]: v };
    setCfg({ ...cfg, areas });
  };
  const setQuestion = (ai, qi, v) => {
    const areas = [...cfg.areas];
    const qs = [...areas[ai].questions];
    qs[qi] = v;
    areas[ai] = { ...areas[ai], questions: qs };
    setCfg({ ...cfg, areas });
  };
  const setBand = (ai, bi, k, v) => {
    const areas = [...cfg.areas];
    const bands = [...areas[ai].bands];
    bands[bi] = { ...bands[bi], [k]: v };
    areas[ai] = { ...areas[ai], bands };
    setCfg({ ...cfg, areas });
  };
  const setOverall = (bi, k, v) => {
    const ob = [...cfg.overall_bands];
    ob[bi] = { ...ob[bi], [k]: v };
    setCfg({ ...cfg, overall_bands: ob });
  };

  const save = async () => {
    setSaving(true);
    try { await adminApi.healthCheck.save(cfg); toast({ title: "Health Check saved" }); }
    catch (e) { toast({ title: "Save failed", variant: "destructive" }); }
    finally { setSaving(false); }
  };

  const uploadPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await adminApi.uploadDocument(fd);
      set("pdf_url", res.url);
      toast({ title: "Workbook uploaded — remember to save" });
    } catch (err) { toast({ title: "Upload failed", variant: "destructive" }); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const removeLead = async (id) => {
    await adminApi.healthCheck.removeLead(id);
    setLeads(leads.filter((l) => l.id !== id));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <button data-testid="hc-tab-config" onClick={() => setTab("config")} className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === "config" ? "bg-navy text-white" : "bg-slate-100 text-slate-600"}`}>Configuration</button>
        <button data-testid="hc-tab-leads" onClick={() => setTab("leads")} className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === "leads" ? "bg-navy text-white" : "bg-slate-100 text-slate-600"}`}>
          <ClipboardList size={14} className="inline mr-1.5" /> Submissions ({leads.length})
        </button>
      </div>

      {tab === "config" && (
        <>
          <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
            <h3 className="font-bold text-navy">Intro</h3>
            <Field label="Eyebrow" value={cfg.intro_eyebrow || ""} onChange={(e) => set("intro_eyebrow", e.target.value)} />
            <Field label="Title" value={cfg.intro_title || ""} onChange={(e) => set("intro_title", e.target.value)} />
            <Area label="Body" rows={3} value={cfg.intro_body || ""} onChange={(e) => set("intro_body", e.target.value)} />
            <Field label="Start button label" value={cfg.start_label || ""} onChange={(e) => set("start_label", e.target.value)} />
          </div>

          {(cfg.areas || []).map((a, ai) => (
            <div key={a.key} className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
              <h3 className="font-bold text-navy">Area: {a.title}</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Title" value={a.title} onChange={(e) => setArea(ai, "title", e.target.value)} />
                <Field label="Icon" value={a.icon} onChange={(e) => setArea(ai, "icon", e.target.value)} />
              </div>
              <Field label="Prompt (question shown above the statements)" value={a.prompt} onChange={(e) => setArea(ai, "prompt", e.target.value)} />
              <div className="space-y-2">
                <Label className="text-navy font-semibold text-sm">Statements (5)</Label>
                {a.questions.map((q, qi) => (
                  <Input key={qi} value={q} onChange={(e) => setQuestion(ai, qi, e.target.value)} data-testid={`hc-q-${a.key}-${qi}`} />
                ))}
              </div>
              <div className="space-y-3">
                <Label className="text-navy font-semibold text-sm">Score bands & advice</Label>
                {a.bands.map((b, bi) => (
                  <div key={bi} className="border border-slate-100 rounded-xl p-3 bg-slate-50 space-y-2">
                    <div className="text-xs font-bold text-slate-500">{b.min}–{b.max} · {b.status}</div>
                    <Field label="Label" value={b.label} onChange={(e) => setBand(ai, bi, "label", e.target.value)} />
                    <Area label="Message" rows={2} value={b.message} onChange={(e) => setBand(ai, bi, "message", e.target.value)} />
                    <Field label="Next step" value={b.next_step} onChange={(e) => setBand(ai, bi, "next_step", e.target.value)} />
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
            <h3 className="font-bold text-navy">Overall score bands</h3>
            {(cfg.overall_bands || []).map((b, bi) => (
              <div key={bi} className="border border-slate-100 rounded-xl p-3 bg-slate-50 space-y-2">
                <div className="text-xs font-bold text-slate-500">{b.min}–{b.max}</div>
                <Field label="Label" value={b.label} onChange={(e) => setOverall(bi, "label", e.target.value)} />
                <Area label="Message" rows={2} value={b.message} onChange={(e) => setOverall(bi, "message", e.target.value)} />
              </div>
            ))}
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
            <h3 className="font-bold text-navy">Gated workbook PDF</h3>
            <p className="text-xs text-slate-400">Shown as a download on the results page (after the visitor completes the check).</p>
            {cfg.pdf_url && (
              <a href={mediaUrl(cfg.pdf_url)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-navy font-semibold">
                <FileText size={15} /> Current workbook
              </a>
            )}
            <div className="flex items-center gap-3">
              <label className={`inline-flex items-center gap-2 ${uploading ? "opacity-60 pointer-events-none" : ""} cursor-pointer bg-navy text-white font-semibold text-sm px-4 py-2.5 rounded-full`}>
                {uploading ? <Loader2 size={15} className="animate-spin" /> : <UploadCloud size={15} />} {cfg.pdf_url ? "Replace PDF" : "Upload PDF"}
                <input type="file" accept="application/pdf" className="hidden" onChange={uploadPdf} />
              </label>
              {cfg.pdf_url && <button onClick={() => set("pdf_url", "")} className="text-sm text-red-500 font-semibold">Remove</button>}
            </div>
            <Field label="Download button label" value={cfg.pdf_label || ""} onChange={(e) => set("pdf_label", e.target.value)} />
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
            <h3 className="font-bold text-navy">Results email (auto follow-up to visitor)</h3>
            <p className="text-xs text-slate-400">Weak areas (amber/red) are automatically included with their advice. The overall band message and CTA are added too.</p>
            <Field label="Subject" value={cfg.email_subject || ""} onChange={(e) => set("email_subject", e.target.value)} />
            <Area label="Intro paragraph" rows={3} value={cfg.email_intro || ""} onChange={(e) => set("email_intro", e.target.value)} />
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="CTA button label" value={cfg.email_cta_label || ""} onChange={(e) => set("email_cta_label", e.target.value)} />
              <Field label="CTA link" value={cfg.email_cta_link || ""} onChange={(e) => set("email_cta_link", e.target.value)} />
            </div>
            <label className="flex items-center gap-2 text-sm text-navy font-semibold">
              <input type="checkbox" checked={cfg.notify_team !== false} onChange={(e) => set("notify_team", e.target.checked)} /> Email the team on each submission
            </label>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
            <h3 className="font-bold text-navy">Results page CTA</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="CTA heading" value={cfg.results_cta_label || ""} onChange={(e) => set("results_cta_label", e.target.value)} />
              <Field label="CTA link" value={cfg.results_cta_link || ""} onChange={(e) => set("results_cta_link", e.target.value)} />
            </div>
          </div>

          <div className="sticky bottom-4">
            <Button data-testid="hc-save-btn" onClick={save} disabled={saving} className="bg-orange hover:bg-orange-dark text-white rounded-full px-8 font-bold shadow-lg">
              {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />} Save Health Check
            </Button>
          </div>
        </>
      )}

      {tab === "leads" && (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {leads.length === 0 ? (
            <div className="p-8 text-center text-slate-400">No submissions yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-3">Who</th>
                  <th className="text-left px-4 py-3">Score</th>
                  <th className="text-left px-4 py-3">Breakdown</th>
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className="border-t border-slate-100" data-testid={`hc-lead-${l.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-navy">{l.name}</div>
                      <div className="text-slate-400 text-xs">{l.business_name || ""}</div>
                      <div className="text-slate-500 text-xs">{l.email}{l.phone ? ` · ${l.phone}` : ""}</div>
                    </td>
                    <td className="px-4 py-3 font-bold text-navy whitespace-nowrap">{l.overall_score}/40<div className="text-xs font-semibold text-orange">{l.overall_label}</div></td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {(l.areas || []).map((a) => `${a.title} ${a.score}`).join(" · ")}
                    </td>
                    <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">{(l.created_at || "").slice(0, 10)}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => removeLead(l.id)} className="text-red-400 hover:text-red-600"><Trash2 size={15} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
